// Centralized Auth Utilities
// Single source of truth for reading the authenticated user from sessionStorage.
// Every module that needs auth data MUST import from here instead of accessing
// sessionStorage directly.

const AUTH_STORAGE_KEY = 'authUser';

type AuthRole = {
  id?: string | number;
  name: string;
};

type AuthUser = {
  id?: string | number;
  token?: string;
  name?: string;
  email?: string;
  document?: string;
  roles?: AuthRole[];
  user?: {
    id?: string | number;
    name?: string;
    email?: string;
  };
  data?: {
    _id?: string;
    first_name?: string;
    email?: string;
  };
};

// Core Utilities

/**
 * Reads and parses the authenticated user from sessionStorage.
 * Returns `null` if no user is stored or parsing fails.
 */
export const getAuthUser = (): AuthUser | null => {
  try {
    const raw = sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
};

/**
 * Returns the auth token or `null` if unavailable.
 */
export const getAuthToken = (): string | null => {
  return getAuthUser()?.token ?? null;
};

/**
 * Returns the user ID (handles nested `user.id` or root `id`).
 * Falls back to `'guest'` when unavailable.
 */
export const getAuthUserId = (): string => {
  const auth = getAuthUser();
  const id = auth?.user?.id ?? auth?.id;
  return id ? String(id) : 'guest';
};

/**
 * Persists updated auth data back to sessionStorage.
 */
export const setAuthUser = (data: AuthUser): void => {
  sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
};

/**
 * Removes auth data from sessionStorage.
 */
export const clearAuthUser = (): void => {
  sessionStorage.removeItem(AUTH_STORAGE_KEY);
};

// Re-exports for backward compatibility

export const getLoggedinUser = getAuthUser;

export type { AuthUser, AuthRole };
